// Typed wrappers for ALL Phase 2 employee RPCs.
// No raw .from('pulses') / .from('conversations') etc. is allowed elsewhere.
import { supabase } from '@/lib/supabase';

export type PulseRow = {
  id: string;
  org_id: string;
  employee_id: string;
  value: number;
  created_at: string;
  created_day: string;
};

export type ConversationRow = {
  id: string;
  org_id: string;
  employee_id: string;
  channel: 'chat' | 'voice';
  started_at: string;
  ended_at: string | null;
  first_user_turn: string | null;
  summary_preview: string | null;
};

export type MemoryRow = {
  kind: 'insight' | 'action';
  id: string;
  theme: string | null;
  sentiment: string | null;
  title: string | null;
  status: string | null;
  created_at: string;
};

export async function submitPulse(orgId: string, value: number) {
  return supabase.rpc('submit_pulse', { _org_id: orgId, _value: value });
}

export async function myPulses(orgId: string, limit = 90) {
  return supabase.rpc('my_pulses', { _org_id: orgId, _limit: limit });
}

export async function startConversation(orgId: string, channel: 'chat' | 'voice' = 'chat') {
  return supabase.rpc('start_conversation', { _org_id: orgId, _channel: channel });
}

export async function addTurn(conversationId: string, role: 'employee' | 'charles', content: string) {
  return supabase.rpc('add_turn', {
    _conversation_id: conversationId,
    _role: role,
    _content: content,
  });
}

export async function myConversations(orgId: string, limit = 50) {
  return supabase.rpc('my_conversations', { _org_id: orgId, _limit: limit });
}

export type SummaryStatus =
  | 'ready'
  | 'processing'
  | 'no_content'
  | 'too_short'
  | 'pending_retry'
  | 'not_ended';

export type ConversationDetail = {
  conversation: {
    id: string;
    channel: 'chat' | 'voice';
    started_at: string;
    ended_at: string | null;
  };
  summary: { key_points: string; created_at: string } | null;
  summary_meta: {
    status: SummaryStatus;
    turn_count: number;
    employee_turn_count: number;
    ended_at: string | null;
  };
  survey: {
    id: string;
    name: string;
    north_vision: string | null;
    anchors: Array<{
      anchor_id: string;
      name: string;
      description: string | null;
      north_vision_target: number | null;
      scale_labels: Record<string, string> | null;
      sort_order: number | null;
      my_value: number | null;
      my_updated_at: string | null;
    }>;
  } | null;
};

export async function myConversationDetail(conversationId: string) {
  return supabase.rpc('my_conversation_detail', { _conversation_id: conversationId });
}

export type AnchorHistoryRow = {
  response_id: string;
  survey_id: string;
  survey_name: string;
  anchor_id: string;
  anchor_name: string;
  value: number;
  recorded_at: string;
};

export async function myAnchorHistory(orgId: string, surveyId: string | null = null) {
  const { data, error } = await supabase.rpc('my_anchor_history', {
    _org_id: orgId,
    _survey_id: surveyId,
  });
  return { data: (data ?? []) as AnchorHistoryRow[], error };
}

export async function requestConversationSummary(conversationId: string) {
  return supabase.functions.invoke('summarize-conversation', {
    body: { conversation_id: conversationId },
  });
}

export async function myMemory(orgId: string) {
  return supabase.rpc('my_memory', { _org_id: orgId });
}

export async function endConversation(conversationId: string) {
  return supabase.rpc('end_conversation', { _conversation_id: conversationId });
}

export async function erasePulse(pulseId: string) {
  return supabase.rpc('erase_pulse', { _pulse_id: pulseId });
}

export async function eraseConversation(conversationId: string) {
  return supabase.rpc('erase_conversation', { _conversation_id: conversationId });
}

export async function logAuditEvent(
  orgId: string,
  action: 'erase_pulse' | 'erase_conversation' | 'charles_waitlist_interest',
  targetId: string | null,
) {
  return supabase.rpc('log_audit_event', {
    _org_id: orgId,
    _action: action,
    _target_id: targetId,
  });
}
